import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import { useAuth0 } from "@auth0/auth0-react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Message, useToaster } from "rsuite";
import { InputContext } from "../../_context/InputContext";
import VisibleIcon from "@rsuite/icons/Visible";
import UnvisibleIcon from "@rsuite/icons/Unvisible";
import {
  SegmentedPicker,
  Button,
  GlassCard,
  SectionTitle,
  Badge,
  Loader,
  NoDataState,
  SpacedGlassCard,
} from "../../../../ui";
import { SystemSummaryFC } from "./SystemSummaryFC";
import { GenerateAcquisition } from "./_helpers/GenerateAcquisition";
import { AcumaticaSettings } from "./AcumaticaSettings";
import { ProcessingSettings } from "./ProcessingSettings";
import { ConfigurationSettings } from "./ConfigurationSettings";
import { EdgeSystemTabFC } from "../EdgeFleet/EdgeSystemTabFC";

export const _NetSystemsFC = (props) => {
  let history = useHistory();
  const { getAccessTokenSilently } = useAuth0();
  const { setAdminViewToggle } = useContext(InputContext);
  const [system, setSystem] = useState();
  const [acumatica, setAcumatica] = useState();
  const [loadingSystemData, setLoadingSystemData] = useState(false);
  const [error, setError] = useState(null);

  // NORMALIZED CONFIGS
  const [configAcquisition, setConfigAcquisition] = useState();
  const [configEDU, setConfigEDU] = useState();
  const [configMag, setConfigMag] = useState();
  const [configLog, setConfigLog] = useState();
  const [regexModels, setRegexModels] = useState();

  const [active, setActive] = useState("Summary");
  const [hasEdge, setHasEdge] = useState(false);
  const [edgeProbeDone, setEdgeProbeDone] = useState(false);
  const location = useLocation();
  const { id } = useParams();
  const toaster = useToaster();

  const getNavigationOptions = () => {
    const base = [
      { eventKey: "Summary",     label: "Summary" },
      { eventKey: "Acumatica",   label: "Acumatica" },
      { eventKey: "Processing",  label: "Processing" },
      { eventKey: "Acquisition", label: "Acquisition" },
      { eventKey: "Configs",     label: "Configs" },
    ];
    if (hasEdge) base.push({ eventKey: "Edge", label: "Edge" });
    return base;
  };

  const getSystemData = async () => {
    setLoadingSystemData(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const res = await axiosInstance({
        url: `/network/system/${id}`,
        headers: { authorization: `Bearer ${token}` },
      });

      setSystem(res?.data[0][0]);
      setAcumatica(res?.data[1][0]);
      setConfigAcquisition(res?.data[2][0]);
      setConfigEDU(res?.data[3][0]);
      setConfigMag(res?.data[4]);
      setConfigLog(res?.data[5]);
    } catch (error) {
      console.error("Failed to fetch system data:", error);
      setError("Failed to load system data. Please try again.");
    }

    setLoadingSystemData(false);
  };

  const getRegexModels = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const res = await axiosInstance({
        url: `/network/regex-models`,
        headers: { authorization: `Bearer ${token}` },
      });

      setRegexModels(res.data[0]);
    } catch (error) {
      console.error("Failed to fetch regex models:", error);
    }
  };

  const probeEdge = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });
      await axiosInstance({
        url: `/edge/system/${id}`,
        headers: { authorization: `Bearer ${token}` },
      });
      setHasEdge(true);
    } catch (e) {
      setHasEdge(false);
    } finally {
      setEdgeProbeDone(true);
    }
  };

  const handleVisibilityToggle = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/network/update/system`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          sme: id,
          updateFields: ["show_on_website"],
        }),
      });

      toaster.push(
        <Message showIcon type="success" header="Success">
          System visibility updated successfully.
        </Message>,
        "topCenter",
      );

      getSystemData();
    } catch (error) {
      console.error("Failed to update visibility:", error);
      toaster.push(
        <Message showIcon type="error" header="Error">
          Failed to update system visibility.
        </Message>,
        "topCenter",
      );
    }
  };

  const getStatusBadgeVariant = (system, acumatica) => {
    if (!acumatica) return "error";

    const hasDiscrepancies =
      system?.cus_sys_id !== acumatica?.customeruniqueid ||
      system?.room !== acumatica?.room ||
      system?.model !== acumatica?.model ||
      system?.serial_number !== acumatica?.serialnumber;

    return hasDiscrepancies ? "warning" : "success";
  };

  const getStatusText = (system, acumatica) => {
    if (!acumatica) return "Not Found";

    const hasDiscrepancies =
      system?.cus_sys_id !== acumatica?.customeruniqueid ||
      system?.room !== acumatica?.room ||
      system?.model !== acumatica?.model ||
      system?.serial_number !== acumatica?.serialnumber;

    return hasDiscrepancies ? "Discrepancies" : "Synced";
  };

  const renderActiveSection = () => {
    if (!system) return null;

    switch (active) {
      case "Summary":
        return <SystemSummaryFC system={system} acumatica={acumatica} />;
      case "Acumatica":
        return (
          <AcumaticaSettings
            system={system}
            acumatica={acumatica}
            onUpdate={getSystemData}
          />
        );
      case "Processing":
        return <ProcessingSettings system={system} onUpdate={getSystemData} />;
      case "Acquisition":
        return (
          <GenerateAcquisition
            acquisition_config={configAcquisition}
            regex_models={regexModels}
          />
        );
      case "Configs":
        return (
          <ConfigurationSettings
            system={system}
            configEDU={configEDU}
            configMag={configMag}
            configLog={configLog}
            regexModels={regexModels}
          />
        );
      case "Edge":
        return <EdgeSystemTabFC sme={id} />;
      default:
        return <SystemSummaryFC system={system} acumatica={acumatica} />;
    }
  };

  useEffect(() => {
    getSystemData();
    getRegexModels();
    probeEdge();
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab) setActive(tab);
  }, [id]);

  if (loadingSystemData) {
    return (
      <LoaderContainer>
        <Loader size="large" message="Loading system data..." />
      </LoaderContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <NoDataState
          title="Error Loading System"
          description={error}
          showRetry
          onRetry={getSystemData}
        />
      </ErrorContainer>
    );
  }

  if (!system && (!edgeProbeDone || !hasEdge)) {
    if (!edgeProbeDone) {
      return (
        <LoaderContainer>
          <Loader size="large" message="Loading system data..." />
        </LoaderContainer>
      );
    }
    return (
      <ErrorContainer>
        <NoDataState
          title="System Not Found"
          description="The requested system could not be found."
        />
      </ErrorContainer>
    );
  }

  if (!system && hasEdge) {
    return (
      <SystemContainer>
        <Header>
          <BackButton
            variant="secondary"
            onClick={() => {
              setAdminViewToggle("EdgeFleet");
              history.push("/admin");
            }}
          >
            ← Back to Fleet
          </BackButton>
          <SectionTitle
            title={id}
            subtitle="Edge-only system"
          />
        </Header>
        <ContentSection>
          <EdgeSystemTabFC sme={id} />
        </ContentSection>
      </SystemContainer>
    );
  }

  return (
    <SystemContainer>
      <Header>
        <BackButton
          variant="secondary"
          onClick={() => {
            setAdminViewToggle("Network");
            history.push("/admin");
          }}
        >
          ← Back
        </BackButton>

        <SectionTitle
          title="System Administration"
          subtitle="Manage system configuration and settings"
        />
      </Header>

      <SystemHeader>
        <SpacedGlassCard>
          <SystemInfo>
            <SystemMainInfo>
              <SystemId>{system.id || "{No System ID}"}</SystemId>
              <SystemSubInfo>
                {acumatica?.customeruniqueid || "{No System Client ID}"}
              </SystemSubInfo>
              <SystemLocation>
                <LocationText>
                  <strong>{system.customer_name}</strong> • {system.site_name}
                </LocationText>
                <EquipmentText>
                  {system.manufacturer} {system.model}
                </EquipmentText>
              </SystemLocation>
            </SystemMainInfo>

            <SystemStatus>
              {/*<StatusGroup>
                <StatusLabel>Acumatica Status</StatusLabel>
                <Badge variant={getStatusBadgeVariant(system, acumatica)}>
                  {getStatusText(system, acumatica)}
                </Badge>
              </StatusGroup>*/}

              <StatusGroup>
                <StatusLabel>Client Visibility</StatusLabel>
                <VisibilityButton
                  variant={system.show_on_website ? "success" : "subtle"}
                  size="small"
                  onClick={handleVisibilityToggle}
                >
                  {system.show_on_website ? (
                    <>
                      <VisibleIcon style={{ marginRight: "0.5rem" }} />
                      Visible
                    </>
                  ) : (
                    <>
                      <UnvisibleIcon style={{ marginRight: "0.5rem" }} />
                      Hidden
                    </>
                  )}
                </VisibilityButton>
              </StatusGroup>
            </SystemStatus>
          </SystemInfo>
        </SpacedGlassCard>
      </SystemHeader>

      <NavigationSection>
        <NavigationContainer>
          <SegmentedPicker
            options={getNavigationOptions()}
            activeKey={active}
            onSelect={setActive}
            size="medium"
            variant="glass"
          />
        </NavigationContainer>
      </NavigationSection>

      <ContentSection>
        <GlassCard>
          <ContentContainer>{renderActiveSection()}</ContentContainer>
        </GlassCard>
      </ContentSection>
    </SystemContainer>
  );
};

// Styled Components
const SystemContainer = styled.div`
  padding: 2rem;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  min-height: 100vh;
  box-sizing: border-box;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto 2rem auto;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const BackButton = styled(Button)`
  @media (max-width: 768px) {
    order: -1;
  }
`;

const SystemHeader = styled.div`
  margin-bottom: 2rem;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto 2rem auto;
`;

const SystemInfo = styled.div`
  padding: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const SystemMainInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SystemId = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  color: rgba(0, 0, 0, 0.9);
`;

const SystemSubInfo = styled.div`
  font-size: 1.1rem;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
`;

const SystemLocation = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const LocationText = styled.div`
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.8);
`;

const EquipmentText = styled.div`
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.6);
`;

const SystemStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: flex-end;

  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
  }
`;

const StatusGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;

  @media (max-width: 768px) {
    align-items: center;
  }
`;

const StatusLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const VisibilityButton = styled(Button)`
  min-width: 120px;
`;

const NavigationSection = styled.div`
  margin-bottom: 2rem;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto 2rem auto;
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const ContentSection = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
`;

const ContentContainer = styled.div`
  padding: 2rem;
  min-height: 500px;
  width: 100%;
  max-width: 1360px;
  margin: 0 auto;
  box-sizing: border-box;

  /* Ensure consistent width regardless of content */
  overflow-x: auto;
`;
